import React, { useEffect, useState, useCallback } from "react";
import {
  adminListDeletionRequests,
  adminApproveDeletionRequest,
  adminRejectDeletionRequest,
  adminListDataExportRequests,
  adminProcessDataExportRequest,
} from "./adminApi";
import "./AdminStyles.css";
import "./AdminGdpr.css";

const EXPORT_STATUS = {
  not_started: "Da preparare", queued: "Accodata", sending: "Invio SMTP in corso",
  retrying: "Nuovo tentativo programmato", failed: "Invio non riuscito",
  review_required: "Esito da verificare manualmente", unavailable: "Stato invio non disponibile",
};
const canProcessExport = request => ["not_started", "failed"].includes(request.delivery_status);
const exportActionLabel = request => request.delivery_status === "failed" ? "Riprova invio" : "Prepara e accoda";

export default function AdminGdpr() {
  const token = localStorage.getItem("token");
  const [activeTab, setActiveTab] = useState("cancellazione");

  const [delRequests, setDelRequests] = useState([]);
  const [delLoading, setDelLoading] = useState(true);
  const [delBusyId, setDelBusyId] = useState(null);
  const [delBusyAction, setDelBusyAction] = useState(null);

  const [expRequests, setExpRequests] = useState([]);
  const [expLoading, setExpLoading] = useState(true);
  const [expBusyId, setExpBusyId] = useState(null);

  const load = useCallback(async () => {
    setDelLoading(true);
    setExpLoading(true);
    try {
      const [delData, expData] = await Promise.all([
        adminListDeletionRequests(token),
        adminListDataExportRequests(token),
      ]);
      setDelRequests(Array.isArray(delData) ? delData : []);
      setExpRequests(Array.isArray(expData) ? expData : []);
    } catch (e) {
      console.error(e);
    } finally {
      setDelLoading(false);
      setExpLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApproveDeletion = async (id) => {
    if (
      !window.confirm(
        "Approvare la cancellazione? L'account verrà anonimizzato immediatamente e non sarà recuperabile.",
      )
    )
      return;
    try {
      setDelBusyId(id);
      setDelBusyAction("approve");
      await adminApproveDeletionRequest(id, token);
      await load();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Errore durante l'approvazione");
    } finally {
      setDelBusyId(null);
      setDelBusyAction(null);
    }
  };

  const handleRejectDeletion = async (id) => {
    const note = window.prompt(
      "Motivo del rifiuto (opzionale — visibile all'utente nell'email):",
      "",
    );
    if (note === null) return;
    try {
      setDelBusyId(id);
      setDelBusyAction("reject");
      await adminRejectDeletionRequest(id, note, token);
      await load();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Errore durante il rifiuto");
    } finally {
      setDelBusyId(null);
      setDelBusyAction(null);
    }
  };

  const handleProcessExport = async (id) => {
    if (
      !window.confirm(
        "Preparare o riprovare l’esportazione? L’accodamento non conferma l’accettazione SMTP né la consegna alla casella.",
      )
    )
      return;
    try {
      setExpBusyId(id);
      await adminProcessDataExportRequest(id, token);
      window.alert("Lavorazione accodata o già in corso. Aggiorna lo stato per verificare l’esito SMTP; la consegna alla casella non è confermata.");
      await load();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Errore durante l'elaborazione");
    } finally {
      setExpBusyId(null);
    }
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  return (
    <div className="admin-page gdpr-page">
      <div className="admin-overlay" />

      <main className="admin-content approvals-content">
        <section className="approvals-shell approvals-shell--narrow">
          <header className="approvals-pageHead">
            <div>
              <h1 className="approvals-pageTitle">Richieste GDPR</h1>
              <p className="approvals-pageSub">
                Gestisci richieste di cancellazione account (Art. 17) e
                portabilità dati (Art. 20).
              </p>
            </div>
          </header>

          {/* Tab switcher */}
          <div className="gdpr-tabs">
            <button
              type="button"
              className={`gdpr-tab ${activeTab === "cancellazione" ? "is-active" : ""}`}
              onClick={() => setActiveTab("cancellazione")}
            >
              Cancellazione account
              {delRequests.length > 0 && (
                <span className="gdpr-tab-badge gdpr-tab-badge--red">
                  {delRequests.length}
                </span>
              )}
            </button>
            <button
              type="button"
              className={`gdpr-tab ${activeTab === "export" ? "is-active" : ""}`}
              onClick={() => setActiveTab("export")}
            >
              Portabilità dati
              {expRequests.length > 0 && (
                <span className="gdpr-tab-badge gdpr-tab-badge--blue">
                  {expRequests.length}
                </span>
              )}
            </button>
          </div>

          {/* ── TAB: CANCELLAZIONE ── */}
          {activeTab === "cancellazione" && (
            <section className="approvals-card">
              <div className="approvals-cardHead">
                <div>
                  <h2 className="approvals-cardTitle">
                    Richieste cancellazione account
                  </h2>
                  <p className="approvals-cardHint">
                    Approva o rifiuta le richieste GDPR Art. 17. Solo
                    l&apos;approvazione attiva la cancellazione effettiva.
                  </p>
                </div>
              </div>

              <div className="approvals-cardBody">
                {delLoading ? (
                  <p>Caricamento…</p>
                ) : delRequests.length === 0 ? (
                  <p>Nessuna richiesta di cancellazione in sospeso.</p>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="approvals-tableDesktop">
                      <div className="approvals-tableWrap approvals-tableWrap--compact">
                        <table className="approvals-table">
                          <thead>
                            <tr>
                              <th className="approvals-th">Utente</th>
                              <th className="approvals-th">Data richiesta</th>
                              <th className="approvals-th">Ruolo</th>
                              <th className="approvals-th">Stato</th>
                              <th className="approvals-th approvals-thActions">
                                Azioni
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {delRequests.map((r) => {
                              const isBusy = delBusyId === r.id;
                              const fullName = [r.name, r.last_name]
                                .filter(Boolean)
                                .join(" ")
                                .trim();
                              return (
                                <tr
                                  key={r.id}
                                  className="approvals-row approvals-rowCompact"
                                >
                                  <td className="approvals-td">
                                    <div className="approvals-mainCell approvals-mainCellCompact">
                                      <span className="approvals-name approvals-nameStrong">
                                        {fullName || "—"}
                                      </span>
                                      <div className="approvals-subline approvals-sublineCompact">
                                        {r.email || "—"}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="approvals-td">
                                    <span className="gdpr-date">
                                      {fmtDate(r.requested_at)}
                                    </span>
                                  </td>
                                  <td className="approvals-td">
                                    <span className="gdpr-role">
                                      {r.role || "—"}
                                    </span>
                                  </td>
                                  <td className="approvals-td">
                                    <span
                                      className="approvals-pendingBadge"
                                      style={{
                                        background: "rgba(220,38,38,0.1)",
                                        color: "#dc2626",
                                      }}
                                    >
                                      In attesa
                                    </span>
                                  </td>
                                  <td className="approvals-td approvals-tdActions">
                                    <div className="approvals-actions approvals-actionsCompact">
                                      <button
                                        type="button"
                                        className="approvals-btn approvals-btnDanger"
                                        onClick={() =>
                                          handleApproveDeletion(r.id)
                                        }
                                        disabled={isBusy}
                                      >
                                        {isBusy &&
                                        delBusyAction === "approve"
                                          ? "Cancellazione…"
                                          : "Approva"}
                                      </button>
                                      <button
                                        type="button"
                                        className="approvals-btn"
                                        onClick={() =>
                                          handleRejectDeletion(r.id)
                                        }
                                        disabled={isBusy}
                                      >
                                        {isBusy && delBusyAction === "reject"
                                          ? "Rifiuto…"
                                          : "Rifiuta"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="approvals-mobileList">
                      {delRequests.map((r) => {
                        const isBusy = delBusyId === r.id;
                        const fullName = [r.name, r.last_name]
                          .filter(Boolean)
                          .join(" ")
                          .trim();
                        return (
                          <article
                            className="approvals-mobileCard"
                            key={r.id}
                          >
                            <div className="approvals-mobileTop">
                              <div className="approvals-mobileTitleWrap">
                                <div className="approvals-mobileMeta">
                                  <span
                                    className="approvals-pendingBadge"
                                    style={{
                                      background: "rgba(220,38,38,0.1)",
                                      color: "#dc2626",
                                    }}
                                  >
                                    Cancellazione
                                  </span>
                                </div>
                                <h3 className="approvals-mobileTitle">
                                  {fullName || "—"}
                                </h3>
                                <p className="approvals-mobileSub">
                                  {r.email || "—"}
                                </p>
                              </div>
                            </div>
                            <div className="approvals-mobileGrid">
                              <div className="approvals-mobileStat">
                                <span className="approvals-mobileLabel">
                                  Ruolo
                                </span>
                                <span className="approvals-mobileValue">
                                  {r.role || "—"}
                                </span>
                              </div>
                              <div className="approvals-mobileStat">
                                <span className="approvals-mobileLabel">
                                  Data richiesta
                                </span>
                                <span className="approvals-mobileValue">
                                  {fmtDate(r.requested_at)}
                                </span>
                              </div>
                            </div>
                            <div className="approvals-mobileActions">
                              <button
                                type="button"
                                className="approvals-btn approvals-btnDanger"
                                onClick={() => handleApproveDeletion(r.id)}
                                disabled={isBusy}
                              >
                                {isBusy && delBusyAction === "approve"
                                  ? "Cancellazione…"
                                  : "Approva ed elimina"}
                              </button>
                              <button
                                type="button"
                                className="approvals-btn"
                                onClick={() => handleRejectDeletion(r.id)}
                                disabled={isBusy}
                              >
                                {isBusy && delBusyAction === "reject"
                                  ? "Rifiuto…"
                                  : "Rifiuta"}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          {/* ── TAB: EXPORT ── */}
          {activeTab === "export" && (
            <section className="approvals-card">
              <div className="approvals-cardHead">
                <div>
                  <h2 className="approvals-cardTitle">
                    Richieste portabilità dati
                  </h2>
                  <p className="approvals-cardHint">
                    Prepara e accoda l&apos;esportazione per l&apos;utente richiedente.
                    La richiesta resta aperta fino all&apos;accettazione SMTP, che non conferma la consegna alla casella.
                  </p>
                  <button type="button" className="approvals-btn" onClick={load} disabled={expLoading}>Aggiorna stato</button>
                </div>
              </div>

              <div className="approvals-cardBody">
                {expLoading ? (
                  <p>Caricamento…</p>
                ) : expRequests.length === 0 ? (
                  <p>Nessuna richiesta export in attesa dell&apos;accettazione SMTP.</p>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="approvals-tableDesktop">
                      <div className="approvals-tableWrap approvals-tableWrap--compact">
                        <table className="approvals-table">
                          <thead>
                            <tr>
                              <th className="approvals-th">Utente</th>
                              <th className="approvals-th">Data richiesta</th>
                              <th className="approvals-th">Ruolo</th>
                              <th className="approvals-th">Stato</th>
                              <th className="approvals-th approvals-thActions">
                                Azioni
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {expRequests.map((r) => {
                              const isBusy = expBusyId === r.id;
                              const fullName = [r.name, r.last_name]
                                .filter(Boolean)
                                .join(" ")
                                .trim();
                              return (
                                <tr
                                  key={r.id}
                                  className="approvals-row approvals-rowCompact"
                                >
                                  <td className="approvals-td">
                                    <div className="approvals-mainCell approvals-mainCellCompact">
                                      <span className="approvals-name approvals-nameStrong">
                                        {fullName || "—"}
                                      </span>
                                      <div className="approvals-subline approvals-sublineCompact">
                                        {r.email || "—"}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="approvals-td">
                                    <span className="gdpr-date">
                                      {fmtDate(r.requested_at)}
                                    </span>
                                  </td>
                                  <td className="approvals-td">
                                    <span className="gdpr-role">
                                      {r.role || "—"}
                                    </span>
                                  </td>
                                  <td className="approvals-td">
                                    <span
                                      className="approvals-pendingBadge"
                                      style={{
                                        background: "rgba(13,110,253,0.1)",
                                        color: "#0d6efd",
                                      }}
                                    >
                                      {isBusy ? "Preparazione…" : EXPORT_STATUS[r.delivery_status] || EXPORT_STATUS.unavailable}
                                    </span>
                                  </td>
                                  <td className="approvals-td approvals-tdActions">
                                    <div className="approvals-actions approvals-actionsCompact">
                                      <button
                                        type="button"
                                        className="approvals-btn approvals-btnPrimary"
                                        onClick={() =>
                                          handleProcessExport(r.id)
                                        }
                                        disabled={isBusy || !canProcessExport(r)}
                                      >
                                        {isBusy
                                          ? "Preparazione…"
                                          : exportActionLabel(r)}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile cards */}
                    <div className="approvals-mobileList">
                      {expRequests.map((r) => {
                        const isBusy = expBusyId === r.id;
                        const fullName = [r.name, r.last_name]
                          .filter(Boolean)
                          .join(" ")
                          .trim();
                        return (
                          <article
                            className="approvals-mobileCard"
                            key={r.id}
                          >
                            <div className="approvals-mobileTop">
                              <div className="approvals-mobileTitleWrap">
                                <div className="approvals-mobileMeta">
                                  <span
                                    className="approvals-pendingBadge"
                                    style={{
                                      background: "rgba(13,110,253,0.1)",
                                      color: "#0d6efd",
                                    }}
                                  >
                                    {isBusy ? "Preparazione…" : EXPORT_STATUS[r.delivery_status] || EXPORT_STATUS.unavailable}
                                  </span>
                                </div>
                                <h3 className="approvals-mobileTitle">
                                  {fullName || "—"}
                                </h3>
                                <p className="approvals-mobileSub">
                                  {r.email || "—"}
                                </p>
                              </div>
                            </div>
                            <div className="approvals-mobileGrid">
                              <div className="approvals-mobileStat">
                                <span className="approvals-mobileLabel">
                                  Ruolo
                                </span>
                                <span className="approvals-mobileValue">
                                  {r.role || "—"}
                                </span>
                              </div>
                              <div className="approvals-mobileStat">
                                <span className="approvals-mobileLabel">
                                  Data richiesta
                                </span>
                                <span className="approvals-mobileValue">
                                  {fmtDate(r.requested_at)}
                                </span>
                              </div>
                            </div>
                            <div className="approvals-mobileActions">
                              <button
                                type="button"
                                className="approvals-btn approvals-btnPrimary"
                                onClick={() => handleProcessExport(r.id)}
                                disabled={isBusy || !canProcessExport(r)}
                              >
                                {isBusy ? "Preparazione…" : exportActionLabel(r)}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </section>
      </main>
    </div>
  );
}
